/**
 * StepTreeView - Renders DAG step tree as indented cards with connector lines.
 *
 * Layout rules:
 *   - A step with exactly one Default child is a "continuation" — renders vertically
 *     at the same indent level with a straight connector. No horizontal drift.
 *   - A step with multiple children or a non-Default condition is a "true branch" —
 *     renders indented so the split is visually obvious.
 *
 * Example:
 *   [Email 1]
 *   │ Default
 *   [Email 2]              ← same indent (continuation)
 *   │ Default
 *   [Email 3]              ← same indent (continuation)
 *     ├ If Opened → [Email 4a]   ← indented (true branch)
 *     └ Default   → [Email 4b]   ← indented (true branch)
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import Button from "../common/Button";
import Card from "../ui/Card";
import Badge from "../ui/Badge";
import StepTypeIcon from "./StepTypeIcon";
import BranchConditionBadge from "./BranchConditionBadge";
import type { SequenceStep } from "../../types/sequence.types";
import { STEP_TYPE_OPTIONS } from "../../types/sequence.types";
import { getLists } from "../../services/lists.service";
import {
  orderConditionalBranches,
  splitBranches,
  validateBranchGroup,
} from "../../utils/branchOrder";

/**
 * Pull a sequence step's list/segment references for cross-checking
 * against the current `lists` table. A step can reference at most one
 * list/segment per call: either via the branch_condition_config
 * (in_segment / not_in_segment) or via the action_config.list_id
 * (add_to_list / remove_from_list).
 */
function getStepListRefs(step: SequenceStep): {
  branchSegmentId?: string;
  actionListId?: string;
} {
  const out: { branchSegmentId?: string; actionListId?: string } = {};
  const bc = step.branch_condition_config as
    | Record<string, unknown>
    | undefined;
  if (
    (step.branch_condition === "in_segment" ||
      step.branch_condition === "not_in_segment") &&
    typeof bc?.segment_id === "string" &&
    bc.segment_id
  ) {
    out.branchSegmentId = bc.segment_id;
  }
  const ac = step.action_config as Record<string, unknown> | undefined;
  if (
    (step.step_type === "add_to_list" ||
      step.step_type === "remove_from_list") &&
    typeof ac?.list_id === "string" &&
    ac.list_id
  ) {
    out.actionListId = ac.list_id;
  }
  return out;
}

/**
 * Children in the order the worker actually evaluates them: conditional
 * branches by branch_order (ties broken stably for display only), then the
 * fallback 'default' branches, which are never part of the order loop.
 *
 * get_sequence_step_tree orders by (depth, branch_order) with no tiebreak, so
 * without this a tied sibling group would render in an arbitrary order too.
 */
function childrenInEvaluationOrder(step: SequenceStep): SequenceStep[] {
  const children = step.children ?? [];
  if (children.length < 2) {
    return children;
  }
  const ordered = orderConditionalBranches(children) as SequenceStep[];
  const fallback = splitBranches(children).fallback as SequenceStep[];
  return [...ordered, ...fallback];
}

/** Count every branch-order / missing-fallback warning across the whole tree. */
function countBranchWarnings(steps: SequenceStep[]): number {
  let count = 0;
  const walk = (s: SequenceStep) => {
    const active = (s.children ?? []).filter((c) => c.is_active !== false);
    count += validateBranchGroup(active).length;
    for (const c of s.children ?? []) {
      walk(c);
    }
  };
  for (const s of steps) {
    walk(s);
  }
  return count;
}

export interface StepCountData {
  active_count: number;
  completed_count: number;
  total_passed: number;
}

interface StepTreeViewProps {
  steps: SequenceStep[];
  onAddStep: (parentStepId: string | null) => void;
  onAddBranch: (parentStepId: string) => void;
  onEditStep: (step: SequenceStep) => void;
  /** Opens the delete dialog for this step (the dialog chooses how to delete). */
  onDeleteStep: (step: SequenceStep) => void;
  readOnly?: boolean;
  stepCounts?: Map<string, StepCountData>;
}

const StepTreeView: React.FC<StepTreeViewProps> = ({
  steps,
  onAddStep,
  onAddBranch,
  onEditStep,
  onDeleteStep,
  readOnly = false,
  stepCounts,
}) => {
  // Cross-check every step's list/segment references against the live
  // lists table. Cached at the React Query level — a sibling
  // StepEditor opening uses the same key. Used to:
  //   1. Render a per-step amber badge inside StepNode when a referenced
  //      list/segment doesn't resolve (deleted post-save).
  //   2. Render a top-level red summary banner counting broken refs so
  //      the user sees them at the sequence level without opening every
  //      step.
  // Wrap in arrow so React Query doesn't pass its QueryFunctionContext
  // as getLists's optional organizationId. See StepEditor for the
  // matching fix + explanation.
  const { data: allLists } = useQuery({
    queryKey: ["lists-for-step-editor"],
    queryFn: () => getLists(),
  });
  const knownListIds = React.useMemo(
    () => new Set((allLists ?? []).map((l) => l.id)),
    [allLists],
  );

  // Walk the tree once to count broken refs across all depths.
  const brokenRefCount = React.useMemo(() => {
    if (!allLists) {
      return 0;
    }
    let count = 0;
    const walk = (s: SequenceStep) => {
      const refs = getStepListRefs(s);
      if (refs.branchSegmentId && !knownListIds.has(refs.branchSegmentId)) {
        count++;
      }
      if (refs.actionListId && !knownListIds.has(refs.actionListId)) {
        count++;
      }
      for (const c of s.children ?? []) {
        walk(c);
      }
    };
    for (const s of steps) {
      walk(s);
    }
    return count;
  }, [steps, allLists, knownListIds]);

  const branchWarningCount = React.useMemo(
    () => countBranchWarnings(steps),
    [steps],
  );

  if (steps.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
          <PlusIcon className="h-8 w-8 text-gray-400" />
        </div>
        <p className="text-gray-500 mb-1">No steps defined yet.</p>
        <p className="text-gray-400 text-sm mb-4">
          Add a root step to start building your sequence.
        </p>
        {!readOnly && (
          <Button variant="primary" onClick={() => onAddStep(null)}>
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Root Step
          </Button>
        )}
      </div>
    );
  }

  return (
    // overflow-x: auto so genuinely wide branching trees scroll rather than clip
    <div className="overflow-x-auto">
      {brokenRefCount > 0 && (
        <div className="mb-3 bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2 text-sm">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-red-900">
            <div className="font-semibold">
              {brokenRefCount} step{brokenRefCount === 1 ? "" : "s"} reference
              {brokenRefCount === 1 ? "s" : ""} a deleted segment or list.
            </div>
            <div className="text-xs text-red-800 mt-0.5">
              Branch conditions resolve to a default outcome (no match) and
              add/remove-list actions fail at runtime. Edit each affected step
              and pick a current segment to fix.
            </div>
          </div>
        </div>
      )}
      {branchWarningCount > 0 && (
        <div className="mb-3 bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2 text-sm">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-amber-900">
            <div className="font-semibold">
              {branchWarningCount} branching problem
              {branchWarningCount === 1 ? "" : "s"} in this sequence.
            </div>
            <div className="text-xs text-amber-800 mt-0.5">
              Branches are checked from the top and the first match wins. The
              steps flagged below either have branches competing for the same
              place in that queue, or no fallback for contacts that match
              nothing.
            </div>
          </div>
        </div>
      )}
      <div className="space-y-2 min-w-0">
        {steps.map((step) => (
          <StepNode
            key={step.id}
            step={step}
            depth={0}
            visualDepth={0}
            isContinuation={false}
            onAddStep={onAddStep}
            onAddBranch={onAddBranch}
            onEditStep={onEditStep}
            onDeleteStep={onDeleteStep}
            readOnly={readOnly}
            stepCounts={stepCounts}
            knownListIds={knownListIds}
          />
        ))}
      </div>
    </div>
  );
};

// Individual step node with recursive children
const StepNode: React.FC<{
  step: SequenceStep;
  /** Structural depth — tracks nesting for collapse behavior */
  depth: number;
  /** Visual indent level — only increments for true branches, not continuations */
  visualDepth: number;
  /**
   * True when this node is the sole Default child of its parent.
   * Renders with a straight vertical connector instead of an angled branch connector
   * and does NOT add horizontal indentation.
   */
  isContinuation: boolean;
  onAddStep: (parentStepId: string | null) => void;
  onAddBranch: (parentStepId: string) => void;
  onEditStep: (step: SequenceStep) => void;
  /** Opens the delete dialog for this step (the dialog chooses how to delete). */
  onDeleteStep: (step: SequenceStep) => void;
  readOnly: boolean;
  stepCounts?: Map<string, StepCountData>;
  /** Set of list ids that currently exist in the lists table. Refs not in
   *  this set get a per-card "missing segment" badge. */
  knownListIds?: Set<string>;
  /** 1-based place of this branch in its parent's evaluation queue. Only set
   *  for conditional branches in a group of 2 or more — the order is
   *  meaningless otherwise, and a 'default' branch is never in the queue. */
  evalPosition?: number;
  /** How many conditional branches share the queue with this one. */
  evalTotal?: number;
}> = ({
  step,
  depth,
  visualDepth,
  isContinuation,
  onAddStep,
  onAddBranch,
  onEditStep,
  onDeleteStep,
  readOnly,
  stepCounts,
  knownListIds,
  evalPosition,
  evalTotal,
}) => {
  const [collapsed, setCollapsed] = React.useState(false);
  const hasChildren = step.children && step.children.length > 0;

  // Render children in the order the worker evaluates them, not the raw order
  // the tree RPC happened to return.
  const orderedChildren = React.useMemo(
    () => childrenInEvaluationOrder(step),
    [step],
  );
  // 1-based evaluation position per conditional child, for the badges below.
  const childPositions = React.useMemo(() => {
    const conditional = orderConditionalBranches(step.children ?? []);
    return {
      total: conditional.length,
      byId: new Map(conditional.map((c, i) => [c.id, i + 1])),
    };
  }, [step]);
  // Warnings about THIS step's split, evaluated against the children the
  // worker would actually see (active only).
  const childBranchWarnings = React.useMemo(
    () =>
      validateBranchGroup(
        (step.children ?? []).filter((c) => c.is_active !== false),
      ),
    [step],
  );

  // A step whose only child is a Default branch is a "continuation" — the next
  // step in a linear sequence. Its child will NOT get extra visual indentation.
  const childIsContinuation =
    hasChildren &&
    step.children!.length === 1 &&
    step.children![0].branch_condition === "default";

  const stepTypeLabel =
    STEP_TYPE_OPTIONS.find((o) => o.value === step.step_type)?.label ||
    step.step_type;

  const formatDelay = (value: number, unit: string): string => {
    if (value === 0) {
      return "immediately";
    }
    const singular = unit.slice(0, -1);
    return `after ${value} ${value === 1 ? singular : unit}`;
  };

  const getStepDescription = (): string => {
    const ac = step.action_config || {};
    switch (step.step_type) {
      case "send_email":
        return (ac.email_subject as string) || "No subject";
      case "send_sms":
        return (
          ((ac.sms_message_override as string) || "").substring(0, 60) ||
          "SMS message"
        );
      case "apply_tag":
        return (ac.tag as string) || "No tag";
      case "remove_tag":
        return `Remove: ${(ac.tag as string) || "No tag"}`;
      case "add_to_list":
        return `List: ${(ac.list_id as string) || "?"}`;
      case "remove_from_list":
        return `Remove from List: ${(ac.list_id as string) || "?"}`;
      case "update_attribute":
        return `${ac.field || "?"} = ${ac.value || "?"}`;
      case "create_task":
        return (ac.task_type as string) || "Task";
      case "wait":
        return `Wait ${formatDelay(step.delay_value, step.delay_unit)}`;
      default:
        return "";
    }
  };

  // Indent is RELATIVE to the parent DOM node (each StepNode is rendered
  // inside its parent's children container). Multiplying by visualDepth
  // would compound 40px per nesting level even on continuation chains —
  // that was the original bug behind progressive indenting in production.
  // Instead: continuations get 0 (sit flush under the parent's indent), and
  // true branches get a single 40px offset relative to the parent.
  // The root step has depth=0 and never adds margin.
  const indentPx = depth === 0 ? 0 : isContinuation ? 0 : 40;

  return (
    <div style={{ marginLeft: indentPx }}>
      {/* Connector — only shown for non-root steps */}
      {depth > 0 &&
        (isContinuation ? (
          // Straight vertical connector for linear continuations
          <div className="flex items-center gap-2 ml-4 mb-1">
            <div className="border-l-2 border-gray-200 h-4" />
            <span className="text-xs text-gray-400 font-medium">Default</span>
          </div>
        ) : (
          // Angled corner connector for true branches
          <div className="flex items-center ml-2 mb-1">
            <div className="w-4 border-t border-l border-gray-300 h-4 rounded-bl" />
            {step.branch_condition !== "default" ? (
              <>
                {evalPosition !== undefined &&
                  evalTotal !== undefined &&
                  evalTotal > 1 && (
                    <span
                      className="ml-1 inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600"
                      title={`Checked ${evalPosition} of ${evalTotal} — the first branch that matches wins`}
                    >
                      {evalPosition} of {evalTotal}
                    </span>
                  )}
                <BranchConditionBadge
                  condition={step.branch_condition}
                  config={
                    step.branch_condition_config as Record<string, unknown>
                  }
                  className="ml-1"
                />
              </>
            ) : (
              <span
                className="ml-1 text-xs text-gray-400 font-medium"
                title="Fallback — used only when no other branch matched"
              >
                Default (fallback)
              </span>
            )}
          </div>
        ))}

      <Card
        variant="outlined"
        padding="sm"
        className={`relative ${!step.is_active ? "opacity-50" : ""} ${(() => {
          const refs = getStepListRefs(step);
          const broken =
            (refs.branchSegmentId &&
              knownListIds &&
              !knownListIds.has(refs.branchSegmentId)) ||
            (refs.actionListId &&
              knownListIds &&
              !knownListIds.has(refs.actionListId));
          return broken ? "ring-2 ring-red-300" : "";
        })()}`}
      >
        {(() => {
          const refs = getStepListRefs(step);
          const brokenBranch =
            refs.branchSegmentId &&
            knownListIds &&
            !knownListIds.has(refs.branchSegmentId);
          const brokenAction =
            refs.actionListId &&
            knownListIds &&
            !knownListIds.has(refs.actionListId);
          if (!brokenBranch && !brokenAction) {
            return null;
          }
          return (
            <div className="mb-2 flex items-center gap-1 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              <ExclamationTriangleIcon className="h-3 w-3 flex-shrink-0" />
              <span>
                {brokenBranch
                  ? "Branch references a deleted segment."
                  : "Action references a deleted list."}{" "}
                Edit this step to fix.
              </span>
            </div>
          );
        })()}
        {childBranchWarnings.length > 0 && (
          <div className="mb-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-900">
            {childBranchWarnings.map((w) => (
              <div key={w.code} className="flex items-start gap-1">
                <ExclamationTriangleIcon className="h-3 w-3 flex-shrink-0 mt-0.5 text-amber-600" />
                <span>
                  <span className="font-semibold">{w.message}</span>{" "}
                  {w.consequence}
                </span>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {/* Collapse toggle for steps with children */}
            {hasChildren ? (
              <button
                onClick={() => setCollapsed(!collapsed)}
                className="p-0.5 hover:bg-gray-100 rounded flex-shrink-0"
              >
                {collapsed ? (
                  <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                )}
              </button>
            ) : (
              <div className="w-5 flex-shrink-0" />
            )}

            {/* Step type icon */}
            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
              <StepTypeIcon
                stepType={step.step_type}
                className="h-4 w-4 text-primary-600"
              />
            </div>

            {/* Step info */}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-gray-900 text-sm truncate">
                  {step.name || stepTypeLabel}
                </span>
                {!step.is_active && (
                  <Badge variant="warning" size="sm">
                    Inactive
                  </Badge>
                )}
                {step.include_voucher && (
                  <Badge variant="info" size="sm">
                    Voucher
                  </Badge>
                )}
                {stepCounts?.has(step.id) &&
                  stepCounts.get(step.id)!.total_passed > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded">
                      <span className="font-medium text-blue-600">
                        {stepCounts.get(step.id)!.active_count}
                      </span>{" "}
                      active
                      <span className="text-gray-300 mx-0.5">|</span>
                      <span className="font-medium text-gray-600">
                        {stepCounts.get(step.id)!.total_passed}
                      </span>{" "}
                      passed
                    </span>
                  )}
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                <span>{stepTypeLabel}</span>
                <span>{formatDelay(step.delay_value, step.delay_unit)}</span>
                {step.step_type !== "wait" && (
                  <span className="truncate max-w-[200px]">
                    {getStepDescription()}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          {!readOnly && (
            <div className="flex items-center gap-1 flex-shrink-0 ml-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onAddBranch(step.id)}
                title="Add child branch"
              >
                <PlusIcon className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEditStep(step)}
              >
                <PencilIcon className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDeleteStep(step)}
                title="Delete step"
                data-testid={`delete-step-${step.id}`}
                className="text-red-500 hover:text-red-700"
              >
                <TrashIcon className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Children */}
      {hasChildren && !collapsed && (
        <div className="mt-1 space-y-1">
          {orderedChildren.map((child) => (
            <StepNode
              key={child.id}
              step={child}
              evalPosition={childPositions.byId.get(child.id)}
              evalTotal={childPositions.total}
              depth={depth + 1}
              // Only add visual indentation for true branches (multiple children
              // or a non-Default condition). A single Default child is a
              // continuation and stays at the same visual level.
              visualDepth={childIsContinuation ? visualDepth : visualDepth + 1}
              isContinuation={childIsContinuation}
              onAddStep={onAddStep}
              onAddBranch={onAddBranch}
              onEditStep={onEditStep}
              onDeleteStep={onDeleteStep}
              readOnly={readOnly}
              stepCounts={stepCounts}
              knownListIds={knownListIds}
            />
          ))}
        </div>
      )}

      {/* Add Step button at leaf nodes */}
      {!hasChildren && !readOnly && depth === 0 && (
        <div className="ml-8 mt-1">
          <button
            onClick={() => onAddBranch(step.id)}
            className="text-xs text-gray-400 hover:text-primary-600 flex items-center gap-1 py-1"
          >
            <PlusIcon className="h-3 w-3" />
            Add branch
          </button>
        </div>
      )}
    </div>
  );
};

export default StepTreeView;
